import "./play-app.less"
import "../customize-bootstrap.less"
import { LanguageContextProvider } from "./language/language"
import NotFoundPage from "./pages/not-found-page"
import ErrorBoundaryPage from "./frames/error-boundary-frame"
import PreviewScreenPage from "./pages/preview-screen-page"
import LoadingPage from "./frames/loading-frame"
import CookieConsent from "./components/cookie-consent"
import { PlayResponsiveContextProvider } from "./play-responsive-context"
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom"
import HistoryPage from "./pages/history-page"
import PlayEnvironment from "./play-environment"
import { serverPlayGetUser } from "utils/server"
import { classNames, getBasename, getHtmlVariant } from "utils/helpers"
import StudentPage from "./pages/student-page"
import PlayErrorNotice from "./components/play-error-notice"
import TeacherPage from "./pages/teacher-page"
import HistoryViewReportPage from "./pages/history-view-report-page"
import CertificatesPage from "./pages/certificates-page"
import ProfilePage from "./pages/profile-page"
import { useEffect } from "react"
import { getGlobalDontPreventBrowserClose } from "play/play-model"
import { usePlayState } from "play/hooks/use-play-state"
import { Variant } from "models/tables"
import { FORCE_NEW_GAME } from "./play-constants"
import usePlayGoogleAnalytics from "play/hooks/use-play-google-analytics"

/**
 * TODO: look at this
 *
 * // what to search for
 * serverPlayJoin
 *
 * // old code
 * serverPlayCheckGameCode
 *  studentCheckGameCodeSuccessful
 *  studentCheckGameCodeFailure
 */

// asd

export default function PlayApp() {
  const {
    isPreview,
    internalPage,
    isTeacherUrl,
    screen,
    modifyState,
  } = usePlayState()

  let preventBrowserClose: boolean
  if (isPreview || internalPage === "FinalScreen") {
    preventBrowserClose = false
  } else if (isTeacherUrl) {
    preventBrowserClose = true
  } else {
    // student
    const playingOnScreen = !!screen
    const notShowingInternalPage = !internalPage

    preventBrowserClose =
      (notShowingInternalPage && playingOnScreen) || internalPage === "Loading"
  }

  const suppressConsole = PlayEnvironment.suppressConsole

  useGetLoginInfo()
  useSuppressConsole(suppressConsole)
  usePreventBrowserClose(preventBrowserClose)

  useEffect(() => {
    const variant = getHtmlVariant()
    const isBusinessVersion = variant === Variant.Business

    modifyState((state) => {
      state.isBusinessVersion = isBusinessVersion
    })
  }, [])

  // NOTE: when we start a new game, we should disable reconnect
  // In order to disable reconnect,
  if (PlayEnvironment.forceNewGame) {
    PlayEnvironment.connectionId = null
    const oldLoc = new URL(location.href)
    oldLoc.searchParams.delete(FORCE_NEW_GAME)
    const newLoc = oldLoc.href
    location.replace(newLoc)
    return null
  }

  return (
    <div
      className={classNames("play-app", {
        "is-preview": isPreview,
      })}
    >
      <PlayResponsiveContextProvider>
        <LanguageContextProvider>
          <ErrorBoundaryPage>
            <>
              <BrowserRouter basename={getBasename()}>
                <Routes>
                  <Route
                    path="/preview/:lessonId"
                    element={<PreviewScreenPage />}
                  />
                  <Route path="/start/:lessonId" element={<TeacherPage />} />
                  <Route
                    path="/history/:reportId"
                    element={<HistoryViewReportPage />}
                  />
                  <Route path="/history" element={<HistoryPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/certificates" element={<CertificatesPage />} />
                  <Route path="/" element={<StudentPage />} />
                  <Route errorElement={<FallbackPage />} />
                </Routes>
                <PageViewTracker />
              </BrowserRouter>
              <PlayErrorNotice />
              <CookieConsent />
            </>
          </ErrorBoundaryPage>
        </LanguageContextProvider>
      </PlayResponsiveContextProvider>
    </div>
  )
}

/**
 * After `Routes`, so that its effect runs after those of the page: a title that
 * the page sets when it mounts is the one in the `page_view`.
 */
function PageViewTracker() {
  usePlayGoogleAnalytics()
  return null
}

function FallbackPage() {
  const location = useLocation()
  console.log(location.pathname)
  const path = location.pathname.toLowerCase()

  if (path === "/history" || path === "/history/") {
    return <LoadingPage />
  } else if (path.startsWith("/history/")) {
    return <LoadingPage />
  } else {
    return <NotFoundPage />
  }
}

function useGetLoginInfo() {
  const { modifyState } = usePlayState()
  useEffect(() => {
    const loc = getBasename()
    const pn = location.pathname
    let relativePath = pn
    if (pn.startsWith(loc)) {
      relativePath = pn.substring(loc.length)
    }

    if (relativePath.toLowerCase().startsWith("/preview/")) {
      // console.log('preview => no user init')
    } else {
      serverPlayGetUser()
        .then((me) => {
          modifyState((state) => {
            state.loggedUser = me
            state.fullName = me.name
            state.loggedUserInfoReceived = true
            state.isBusinessVersion = !!me.isBusinessUser
          })
        })
        .catch((e) => {
          modifyState((state) => {
            state.loggedUser = null
            state.fullName = ""
            state.loggedUserInfoReceived = false
          })
        })
    }
  }, [])
}

function useSuppressConsole(suppressConsole: boolean) {
  useEffect(() => {
    const originals = {
      log: window.console.log,
      warn: window.console.warn,
      error: window.console.error,
    }

    function ignore() {}

    if (suppressConsole) {
      window.console.log = ignore
      window.console.warn = ignore
      window.console.error = ignore
    }

    return () => {
      window.console.log = originals.log
      window.console.warn = originals.warn
      window.console.error = originals.error
    }
  }, [suppressConsole])
}

function usePreventBrowserClose(preventBrowserClose: boolean) {
  const globalDontPreventBrowserClose = getGlobalDontPreventBrowserClose()
  if (typeof globalDontPreventBrowserClose === "boolean") {
    preventBrowserClose = !globalDontPreventBrowserClose
  }

  useEffect(() => {
    console.log("preventBrowserClose", preventBrowserClose)

    function handleBeforeUnload(e: BeforeUnloadEvent) {
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event

      let prevent = preventBrowserClose
      let globalDontPrevent = getGlobalDontPreventBrowserClose()

      if (typeof globalDontPrevent === "boolean") {
        prevent = !globalDontPrevent
      }

      if (prevent) {
        console.log("Trying to prevent browser close")
        e.preventDefault()
        e.returnValue = "Unsaved changes"
        return e.returnValue
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [preventBrowserClose])
}
